import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, useAuthStore, useSystemNotificationStore } from '@dukene/shared';


interface UseMerchantSSEOptions {
    onOrderCreated?: (data: any) => void;
    onOrderUpdated?: (data: any) => void;
    onPaymentCompleted?: (data: any) => void;
    onPaymentFailed?: (data: any) => void;
    enabled?: boolean;
}

/**
 * Hook to connect to merchant SSE for real-time updates
 * Automatically invalidates React Query caches when events are received
 */
export function useMerchantSSE(options: UseMerchantSSEOptions = {}) {
    const {
        onOrderCreated,
        onOrderUpdated,
        onPaymentCompleted,
        onPaymentFailed,
        enabled = true,
    } = options;

    const queryClient = useQueryClient();
    const activeStoreId = useAuthStore((state) => state.activeStoreId);
    const eventSourceRef = useRef<EventSource | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const maxReconnectAttempts = 5;

    // Use refs for callbacks to avoid re-triggering connect on every render
    const callbacksRef = useRef({
        onOrderCreated,
        onOrderUpdated,
        onPaymentCompleted,
        onPaymentFailed,
    });

    // Update refs on each render
    useEffect(() => {
        callbacksRef.current = {
            onOrderCreated,
            onOrderUpdated,
            onPaymentCompleted,
            onPaymentFailed,
        };
    });

    const connect = useCallback(async () => {
        if (!enabled) return;

        // Ensure we have a valid token before connecting
        // Native EventSource doesn't support Authorization header, so we pass token in query string
        // If the token is expired, this will refresh it.
        const token = await apiClient.ensureValidToken();
        const storeId = activeStoreId || localStorage.getItem('activeStoreId');

        if (!token) {
            console.error('[SSE] Cannot connect: No valid token available');
            return;
        }

        // Get API URL from env
        let baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
        const sseUrl = `${baseUrl}/merchant/sse`;
        
        const params = new URLSearchParams();
        params.append('token', token);
        if (storeId) params.append('storeId', storeId);
        
        const sseUrlWithAuth = `${sseUrl}?${params.toString()}`;
 
        console.log('[SSE] Connecting to:', sseUrlWithAuth);

        // Create EventSource
        const eventSource = new EventSource(sseUrlWithAuth);

        eventSourceRef.current = eventSource;

        // Connection opened
        eventSource.addEventListener('open', () => {
            console.log('[SSE] Connection established');
            reconnectAttemptsRef.current = 0;
        });

        // Connected event
        eventSource.addEventListener('connected', (event) => {
            const data = JSON.parse(event.data);
            console.log('[SSE] Connected:', data);
        });

        // Order created event
        eventSource.addEventListener('order-created', (event) => {
            const data = JSON.parse(event.data);
            console.log('[SSE] Order created:', data);

            // Invalidate orders query to refetch
            queryClient.invalidateQueries({ queryKey: ['merchant-orders', storeId] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats', storeId] });
            queryClient.invalidateQueries({ queryKey: ['analytics-stats', storeId] });
            void useSystemNotificationStore.getState().fetchNotifications();

            callbacksRef.current.onOrderCreated?.(data);
            window.dispatchEvent(new CustomEvent('merchant-sse-event', { detail: { type: 'order-created', data } }));
        });

        // Order updated event
        eventSource.addEventListener('order-updated', (event) => {
            const data = JSON.parse(event.data);
            console.log('[SSE] Order updated:', data);

            // Invalidate orders queries
            queryClient.invalidateQueries({ queryKey: ['merchant-orders', storeId] });
            queryClient.invalidateQueries({ queryKey: ['order', storeId, data.orderId] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats', storeId] });
            queryClient.invalidateQueries({ queryKey: ['analytics-stats', storeId] });
            void useSystemNotificationStore.getState().fetchNotifications();

            callbacksRef.current.onOrderUpdated?.(data);
            window.dispatchEvent(new CustomEvent('merchant-sse-event', { detail: { type: 'order-updated', data } }));
        });

        // Payment completed event
        eventSource.addEventListener('payment-completed', (event) => {
            const data = JSON.parse(event.data);
            console.log('[SSE] Payment completed:', data);

            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['merchant-orders', storeId] });
            queryClient.invalidateQueries({ queryKey: ['order', storeId, data.orderId] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-stats', storeId] });
            queryClient.invalidateQueries({ queryKey: ['analytics-stats', storeId] });
            queryClient.invalidateQueries({ queryKey: ['revenue-chart', storeId] });
            void useSystemNotificationStore.getState().fetchNotifications();

            callbacksRef.current.onPaymentCompleted?.(data);
            window.dispatchEvent(new CustomEvent('merchant-sse-event', { detail: { type: 'payment-completed', data } }));
        });

        // Payment failed event
        eventSource.addEventListener('payment-failed', (event) => {
            const data = JSON.parse(event.data);
            console.log('[SSE] Payment failed:', data);

            // Invalidate orders query
            queryClient.invalidateQueries({ queryKey: ['merchant-orders', storeId] });
            queryClient.invalidateQueries({ queryKey: ['order', storeId, data.orderId] });

            callbacksRef.current.onPaymentFailed?.(data);
            window.dispatchEvent(new CustomEvent('merchant-sse-event', { detail: { type: 'payment-failed', data } }));
        });

        // Error handling
        eventSource.addEventListener('error', (error) => {
            console.error('[SSE] Connection error:', error);

            // Close the connection
            eventSource.close();

            // Attempt to reconnect with exponential backoff
            if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
                console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);

                reconnectTimeoutRef.current = setTimeout(() => {
                    reconnectAttemptsRef.current++;
                    connect();
                }, delay);
            } else {
                console.error('[SSE] Max reconnection attempts reached');
            }
        });

        return eventSource;
    }, [activeStoreId, enabled, queryClient]);

    useEffect(() => {
        if (!enabled) return;

        connect();

        // Cleanup on unmount
        return () => {
            console.log('[SSE] Disconnecting');
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [connect, enabled]);

    return {
        isConnected: eventSourceRef.current?.readyState === EventSource.OPEN,
        disconnect: () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
        },
        reconnect: () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
            reconnectAttemptsRef.current = 0;
            connect();
        },
    };
}
